import React, { useState } from "react";
import { Box } from "@mui/material";
import { useTranslation } from "react-i18next";
import { Account, AccountTypeEnum } from "@rentablo/api/apiv1";
import { usePurgeAccount } from "@api/v1/hooks/useAccounts";
import { Banner } from "@rentablo/components";
import { ConfirmDeleteDialog } from "@components";
import { AccountEditCard } from "./AccountEditCard";
import { DeleteAccountDialog } from "./DeleteAccountDialog";

interface DangerZoneCardProps {
  account?: Account;
  type: "account" | "depot";
}

const getPurgeCopy = (accountType: AccountTypeEnum | undefined) => {
  if (accountType === "01_depot") {
    return {
      title: "createAndEditAccount.actions.deleteAllInvestments",
      modalTitle: "createAndEditAccount.deleteDialog.titleInvestments",
      modalBody: "createAndEditAccount.deleteDialog.questionInvestments",
    };
  }
  if (accountType === "02_cash") {
    return {
      title: "createAndEditAccount.actions.deleteAllCashTransactions",
      modalTitle: "createAndEditAccount.deleteDialog.titleAllCashTransactions",
      modalBody: "createAndEditAccount.deleteDialog.questionCashTransaction",
    };
  }
  return null;
};

export const DangerZoneCard = ({ account, type }: DangerZoneCardProps) => {
  const { t } = useTranslation();

  const [purgeDialogOpen, setPurgeDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  const {
    mutate: purge,
    isPending: isPurging,
    isError: isPurgeError,
    error: purgeError,
    reset: resetPurge,
  } = usePurgeAccount({ onSuccess: () => setPurgeDialogOpen(false) });

  if (!account) return null;

  const purgeCopy = getPurgeCopy(account.type);
  const deleteCopy = `createAndEditAccount.dangerZone.deleteAccount.${type}`;
  const isReferenceAccount = (account.depotIds?.length ?? 0) > 0;

  const handlePurge = () => {
    purge(String(account.id));
  };

  return (
    <AccountEditCard
      title={t(`createAndEditAccount.dangerZone.${type}Title`)}
      testId="danger-zone-card"
    >
      <Box display="flex" flexDirection="column" gap={3}>
        {purgeCopy && (
          <Banner
            type="warn"
            text={t(purgeCopy.title)}
            displayDefaultIcon
            primaryButton={{
              label: t("createAndEditAccount.actions.delete"),
              onClick: () => {
                resetPurge();
                setPurgeDialogOpen(true);
              },
              testId: "delete-all-button",
            }}
          />
        )}
        <Banner
          type="error"
          title={t(`${deleteCopy}.title`)}
          text={
            isReferenceAccount
              ? t("createAndEditAccount.dangerZone.deleteAccount.boundMessage")
              : t(`${deleteCopy}.description`)
          }
          displayDefaultIcon
          primaryButton={{
            label: t(`${deleteCopy}.button`),
            onClick: () => setDeleteDialogOpen(true),
            testId: "delete-account-button",
            color: "error",
            disabled: isReferenceAccount,
          }}
        />
      </Box>

      {purgeCopy && (
        <ConfirmDeleteDialog
          confirmButtonText={t("createAndEditAccount.deleteDialog.delete")}
          cancelButtonText={t("createAndEditAccount.deleteDialog.close")}
          isOpen={purgeDialogOpen}
          onClose={() => setPurgeDialogOpen(false)}
          onDelete={handlePurge}
          text={t(purgeCopy.modalBody)}
          isDeleteRequestLoading={isPurging}
          title={t(purgeCopy.modalTitle)}
          errorMessage={
            isPurgeError ? purgeError?.response?.data?.message : undefined
          }
          testId="delete-confirmation-dialog"
        />
      )}

      <DeleteAccountDialog
        isOpen={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        accountId={account.id}
        type={type}
      />
    </AccountEditCard>
  );
};
